import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PaymentDialog from "../components/PaymentDialog";
import HardDeleteDialog from "../components/HardDeleteDialog";
import { Plus, Trash2, Download, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { buildPublicReceiptUrl } from "../lib/publicUrl";
import { shareItem } from "../lib/native";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const MODES = ["Cash", "Bank", "Easypaisa", "JazzCash"];

export default function Payments() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("");
  const [from_date, setFrom] = useState("");
  const [to_date, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const params = {};
    if (mode) params.mode = mode;
    if (from_date) params.from_date = from_date;
    if (to_date) params.to_date = to_date;
    const { data } = await api.get("/payments", { params });
    setRows(data);
  };
  useEffect(() => { load(); }, [mode, from_date, to_date]);

  const total = rows.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Payments</h1>
          <p className="text-slate-500 mt-1">All collections across your shop.</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="new-payment-btn">
          <Plus className="w-4 h-4 mr-1" /> Record Payment
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[140px] sm:flex-none sm:w-40">
            <label className="text-xs font-semibold text-slate-600">From</label>
            <Input type="date" value={from_date} onChange={(e) => setFrom(e.target.value)} className="w-full" data-testid="pay-from-input" />
          </div>
          <div className="space-y-1 flex-1 min-w-[140px] sm:flex-none sm:w-40">
            <label className="text-xs font-semibold text-slate-600">To</label>
            <Input type="date" value={to_date} onChange={(e) => setTo(e.target.value)} className="w-full" data-testid="pay-to-input" />
          </div>
          <div className="flex gap-1.5 flex-wrap w-full sm:w-auto">
            <button onClick={() => setMode("")} className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${mode === "" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`} data-testid="filter-mode-all">All modes</button>
            {MODES.map((m) => (
              <button key={m} onClick={() => setMode(m)} className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${mode === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`} data-testid={`filter-mode-${m}`}>{m}</button>
            ))}
          </div>
          <div className="sm:ml-auto text-sm w-full sm:w-auto">
            <span className="text-slate-500">Total:</span>{" "}
            <span className="tabular font-bold text-emerald-700">{fmtPKR(total)}</span>{" "}
            <span className="text-slate-400">· {rows.length} entries</span>
          </div>
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Mode</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No payments.</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payments-row-${p.id}`}>
                  <td className="px-4 py-3 tabular">{p.date}</td>
                  <td className="px-4 py-3 font-medium">{p.customer_name}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold text-emerald-700">{fmtPKR(p.amount)}</td>
                  <td className="px-4 py-3">{p.mode}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{p.note || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      <a
                        href={`${API_BASE}/api/payments/${p.id}/receipt.pdf?shop=${user?.shop_id}`}
                        target="_blank" rel="noreferrer" title="Download receipt"
                        className="text-slate-500 hover:text-emerald-700 p-1 rounded hover:bg-emerald-50"
                        data-testid={`pay-receipt-${p.id}`}>
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        type="button"
                        onClick={() => shareItem({
                          title: "Payment Receipt",
                          text: `Receipt — PKR ${Number(p.amount).toLocaleString()} dated ${p.date} (${p.customer_name})`,
                          url: buildPublicReceiptUrl(p.id, user?.shop_id),
                          dialogTitle: "Share receipt",
                        }).then((r) => { if (r === "copied") toast.success("Receipt link copied"); })}
                        title="Share receipt"
                        className="text-slate-500 hover:text-sky-700 p-1 rounded hover:bg-sky-50"
                        data-testid={`pay-share-${p.id}`}>
                        <Share2 className="w-4 h-4" />
                      </button>
                      {p.customer_mobile && (
                        <a
                          href={`https://wa.me/${(p.customer_mobile || "").replace(/\D/g, "").replace(/^0/, "92")}?text=${encodeURIComponent(`Assalam o Alaikum ${p.customer_name},\n\nAap ki payment receipt PKR ${Number(p.amount).toLocaleString()} dated ${p.date}:\n${buildPublicReceiptUrl(p.id, user?.shop_id)}\n\nShukriya — ${user?.shop_name || "HisaabPro"}`)}`}
                          target="_blank" rel="noreferrer" title="Send receipt via WhatsApp"
                          className="text-emerald-600 hover:text-emerald-800 p-1 rounded hover:bg-emerald-50"
                          data-testid={`pay-wa-${p.id}`}>
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      )}
                      {isOwner && (
                        <button onClick={() => setDeleteTarget(p)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`pay-del-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="payments-mobile-list">
          {rows.length === 0 && (
            <div className="px-4 py-10 text-center text-slate-500 text-sm">No payments.</div>
          )}
          {rows.map((p) => (
            <div key={p.id} className="px-4 py-3" data-testid={`payments-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{p.customer_name}</div>
                  <div className="text-xs text-slate-500 tabular mt-0.5">{p.date} · {p.mode}</div>
                  {p.note && <div className="text-xs text-slate-500 mt-1 truncate">{p.note}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-bold tabular text-emerald-700">{fmtPKR(p.amount)}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <a
                  href={`${API_BASE}/api/payments/${p.id}/receipt.pdf?shop=${user?.shop_id}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-md border border-slate-200 text-slate-700 active:bg-slate-50"
                  data-testid={`pay-receipt-m-${p.id}`}
                >
                  <Download className="w-3.5 h-3.5" /> Receipt
                </a>
                {p.customer_mobile && (
                  <a
                    href={`https://wa.me/${(p.customer_mobile || "").replace(/\D/g, "").replace(/^0/, "92")}?text=${encodeURIComponent(`Assalam o Alaikum ${p.customer_name},\n\nAap ki payment receipt PKR ${Number(p.amount).toLocaleString()} dated ${p.date}:\n${buildPublicReceiptUrl(p.id, user?.shop_id)}\n\nShukriya — ${user?.shop_name || "HisaabPro"}`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-md border border-emerald-200 text-emerald-700 active:bg-emerald-50"
                    data-testid={`pay-wa-m-${p.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                )}
                {isOwner && (
                  <button
                    onClick={() => setDeleteTarget(p)}
                    className="ml-auto inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-md border border-red-200 text-red-700 active:bg-red-50"
                    data-testid={`pay-del-m-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <PaymentDialog open={open} onOpenChange={setOpen} onSaved={load} />

      <HardDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Payment"
        targetLabel={deleteTarget ? `PKR ${deleteTarget.amount} · ${deleteTarget.date}` : ""}
        endpoint={deleteTarget ? `/payments/${deleteTarget.id}` : ""}
        description="Yeh payment permanently delete ho jayegi. Loan ka remaining amount re-calculate ho jayega."
        onDeleted={() => { setDeleteTarget(null); toast.success("Payment removed"); load(); }}
      />
    </div>
  );
}
